import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check, Minus } from 'lucide-react';
import { Button, Card, PageHeader, Section } from '@/components/ui';
import { mission, positioning, strategy, vision } from '@/content/strategy';
import { SITE } from '@/lib/site';

const jump = [
  ['vision', 'Vision'],
  ['mission', 'Mission'],
  ['positioning', 'Positioning'],
  ['strategy', 'Strategy'],
] as const;

export function VisionPage() {
  return (
    <div>
      <PageHeader
        kicker="Vision · Mission · Positioning · Strategy"
        title={<span>Where Fnk is going, <span className="text-accent-ink">and the order it gets there in.</span></span>}
        lead="What Fnk is for, who it is for, and the sequence of decisions that takes it from one benchmark and one customer to the layer data centres standardise on."
        actions={
          <nav className="grid w-full grid-cols-2 gap-px border border-line bg-line sm:flex sm:w-auto">
            {jump.map(([id, label], i) => (
              <a key={id} href={`#${id}`} className="flex items-center gap-3 bg-paper px-4 py-3 text-sm text-ink-2 transition-colors hover:text-ink">
                <span className="font-mono text-xs tabular text-accent-ink">{String(i + 1).padStart(2, '0')}</span>
                {label}
              </a>
            ))}
          </nav>
        }
      />

      <Section id="vision" n="01" kicker="Vision" title="The interface becomes a conversation. The service behind it, an agentic workload.">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          <Card variant="accent" className="p-6 sm:p-8 lg:col-span-3">
            <p className="text-xl font-semibold leading-snug tracking-tight sm:text-2xl">{vision.statement}</p>
          </Card>
          <div className="space-y-4 text-sm leading-relaxed text-ink-2 lg:col-span-2">
            {vision.body.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
        </div>
      </Section>

      <Section id="mission" n="02" kicker="Mission" title="Governed, private, efficient — and proved." tone="white">
        <Card variant="accent" className="mb-6 p-6 sm:p-8">
          <p className="text-lg font-semibold leading-snug tracking-tight sm:text-xl">{mission.statement}</p>
        </Card>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {mission.parts.map(([t, d]) => (
            <Card key={t} variant="plain" className="p-6">
              <h3 className="text-base font-semibold tracking-tight">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-2">{d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section id="positioning" n="03" kicker="Positioning" title={positioning.oneLine} lead={positioning.statement}>
        <p className="label mb-4">{positioning.category}</p>
        <Card variant="raised" className="overflow-x-auto">
          <table className="w-full min-w-[40rem] text-left text-sm">
            <thead>
              <tr className="border-b border-line">
                <th className="label px-5 py-3 font-normal">Buyer</th>
                <th className="label px-5 py-3 font-normal">What Fnk is to them</th>
                <th className="label px-5 py-3 font-normal">Form of sale</th>
              </tr>
            </thead>
            <tbody>
              {positioning.buyers.map(([buyer, line, form]) => (
                <tr key={buyer} className="border-b border-line last:border-b-0">
                  <td className="px-5 py-4 text-ink-2">{buyer}</td>
                  <td className="px-5 py-4 font-medium text-ink">{line}</td>
                  <td className="px-5 py-4 font-mono text-xs text-ink-3">{form}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <Card variant="plain" className="p-6">
            <p className="label mb-4">Fnk is</p>
            <ul className="space-y-3">
              {positioning.is.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-ink-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-ok" />
                  {item}
                </li>
              ))}
            </ul>
          </Card>
          <Card variant="plain" className="p-6">
            <p className="label mb-4">Fnk is not</p>
            <ul className="space-y-3">
              {positioning.isNot.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-ink-3">
                  <Minus className="mt-0.5 h-4 w-4 shrink-0 text-ink-4" />
                  {item}
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <p className="mt-6 max-w-3xl text-sm leading-relaxed text-ink-3">
          Measured on 21 August 2026 at <span className="font-mono tabular text-ink">2.0×</span> the completed requests per second of Spring MVC +
          LangChain4j + vLLM on the same NVIDIA GPU — a whole-stack result, one run per point, not an SLA, and not yet independently reproduced.{' '}
          <Link to="/benchmark" className="text-accent-ink underline decoration-accent/40 underline-offset-2">The benchmark</Link>.
        </p>
      </Section>

      <Section id="strategy" n="04" kicker="Strategy" title="References first. The order is the strategy." lead={strategy.lead} tone="white">
        <p className="label mb-4">Five choices</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {strategy.choices.map(([t, d], i) => (
            <Card key={t} variant="plain" className="p-6">
              <span className="font-mono text-xs tabular text-accent-ink">{String(i + 1).padStart(2, '0')}</span>
              <h3 className="mt-2 text-base font-semibold tracking-tight">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-2">{d}</p>
            </Card>
          ))}
        </div>

        <p className="label mb-4 mt-10">The sequence</p>
        <ol className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {strategy.phases.map(([n, t, d]) => (
            <li key={n}>
              <Card variant="raised" className="flex h-full flex-col p-6">
                <span className="font-mono text-xs text-accent-ink">Phase {n}</span>
                <h3 className="mt-2 text-lg font-semibold tracking-tight">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-2">{d}</p>
              </Card>
            </li>
          ))}
        </ol>

        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card variant="plain" className="p-6">
            <p className="label mb-4">Where we do not compete</p>
            <ul className="space-y-2">
              {strategy.notCompeting.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-ink-3">
                  <Minus className="mt-0.5 h-4 w-4 shrink-0 text-ink-4" />
                  {item}
                </li>
              ))}
            </ul>
          </Card>
          <Card variant="ink" className="p-6">
            <p className="label mb-2">Where it leads</p>
            <p className="text-sm leading-relaxed text-ink-2 sm:text-base">
              <span className="font-semibold text-ink">The standard inference layer.</span> {strategy.longTerm}
            </p>
          </Card>
        </div>
      </Section>

      <section className="border-t border-line">
        <div className="mx-auto max-w-site px-4 py-14 sm:px-6 md:py-20">
          <Card variant="accent" className="flex flex-col gap-6 p-6 sm:p-8 md:flex-row md:items-center md:justify-between md:p-10">
            <div>
              <p className="label mb-2">The first step is small</p>
              <p className="max-w-xl text-sm leading-relaxed text-ink-2">
                An assessment replays your own workload on the Fnk stack against the one you run today, and ends in written acceptance thresholds.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <a href={SITE.assessmentMailto} className="w-full sm:w-auto">
                <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowUpRight className="h-4 w-4" />}>Request an assessment</Button>
              </a>
              <Link to="/about" className="w-full sm:w-auto">
                <Button variant="secondary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="h-4 w-4" />}>About Fnk</Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}
